import { Box, Button, CheckOutline, Flex, Menu, MenuButton, MenuDivider, MenuItem, MenuList, Text } from '@bonusly/design-system';

export const BIZY_DEFAULT_MODEL = 'gpt-5.6-luna';
const BIZY_MODEL_STORAGE_KEY_PREFIX = 'bonusly:bizy:model:';
export const BIZY_FEATURED_MODELS = ['gpt-5.6-luna', 'gpt-5.4', 'gpt-5.4-nano', 'gpt-5.4-mini', 'qwen/qwen3.7', 'gpt-5.5'] as const;
const BIZY_STANDARD_MODELS = [BIZY_DEFAULT_MODEL] as const;
export const BIZY_ALLOWED_MODELS = new Set<string>([...BIZY_FEATURED_MODELS, ...BIZY_STANDARD_MODELS]);

function bizyModelStorageKey(userId: string): string {
	return `${BIZY_MODEL_STORAGE_KEY_PREFIX}${userId}`;
}

export function loadStoredBizyModel(userId: string): string {
	try {
		const storedModel = window.localStorage.getItem(bizyModelStorageKey(userId));
		return storedModel && BIZY_ALLOWED_MODELS.has(storedModel) ? storedModel : BIZY_DEFAULT_MODEL;
	} catch {
		return BIZY_DEFAULT_MODEL;
	}
}

export function saveStoredBizyModel(userId: string, model: string) {
	try {
		window.localStorage.setItem(bizyModelStorageKey(userId), model);
	} catch {
		// Ignore storage failures; the in-memory selection still applies.
	}
}

export function BizyModelSelector({
	selectedModel,
	onSelectModel,
	isDisabled,
}: {
	selectedModel: string;
	onSelectModel: (model: string) => void;
	isDisabled?: boolean;
}) {
	const renderModelItem = (model: string, key: string) => (
		<MenuItem key={key} _focus={{ bg: 'transparent' }} onClick={() => onSelectModel(model)}>
			<Flex align="center" justify="space-between" gap={3} w="100%">
				<Text>{model}</Text>
				{selectedModel === model && <CheckOutline boxSize={4} />}
			</Flex>
		</MenuItem>
	);

	return (
		<Menu placement="top-start" autoSelect={false}>
			<MenuButton
				as={Button}
				size="xs"
				variant="ghost"
				bg="secondary.bgAlpha"
				borderRadius="6px"
				px={2}
				py={0.5}
				h="auto"
				minH="24px"
				color="text.soft"
				fontWeight="normal"
				isDisabled={isDisabled}
				aria-label="Select Bizy model"
				_hover={{ bg: 'secondary.bgAlphaHover', color: 'text.default' }}
			>
				{selectedModel}
			</MenuButton>
			<MenuList minW="220px" bg="bg.surface" borderColor="border.soft" p={2}>
				<Box px={3} py={1}>
					<Text fontSize="xs" color="text.soft" fontWeight="semibold">
						Featured
					</Text>
				</Box>
				{BIZY_FEATURED_MODELS.map((model) => renderModelItem(model, `featured-${model}`))}
				<MenuDivider />
				{BIZY_STANDARD_MODELS.map((model) => renderModelItem(model, `standard-${model}`))}
			</MenuList>
		</Menu>
	);
}
